// @ts-nocheck
/**
 * Creates or edits a scoped, expiring, revocable link to a Live Sheet.
 *
 * Reopening Share on a sheet that already has links lists them first, so the
 * organiser can see who currently has access and edit that token rather than
 * silently minting a second one.
 */
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Copy, Loader2, Plus, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import SendShareLinkForm from "@/components/events/SendShareLinkForm";
import RecipientChipsInput from "@/components/events/RecipientChipsInput";
import { getSessionUser } from '@/lib/authSession';

export const shareSheetUrl = (token: string) =>
  `${window.location.origin}/sheet/share/${token}`;

export const SHEET_SHARE_SCOPE = "event";

/** Links must be distinguishable in a list — never save a null label. */
export const defaultShareLabel = (d: Date = new Date()) =>
  `Shared ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;

type Audience = "named" | "anyone";
type Expiry = "event" | "7days" | "custom" | "never";

const EXPIRY_LABELS: Record<Expiry, string> = {
  event: "Day after the event",
  "7days": "In 7 days",
  custom: "Custom date",
  never: "Never",
};

const toDateInput = (iso?: string | null) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

export default function ShareSheetDialog({
  eventId,
  open,
  onOpenChange,
  onCreated,
  /** A link carries the tab's scope, so it reopens on the tab it was made from. */
  scope = SHEET_SHARE_SCOPE,
  /** The exact tab key — scope is coarse and can't tell Media from Teams. */
  tab,
}: {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
  scope?: string;
  tab?: string;
}) {
  const [mode, setMode] = useState<"list" | "form">("form");
  const [tokens, setTokens] = useState<any[]>([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [audience, setAudience] = useState<Audience>("named");
  const [emails, setEmails] = useState<string[]>([]);
  const [permission, setPermission] = useState<"view" | "edit">("view");
  const [hideMoney, setHideMoney] = useState(false);
  const [hideCheckin, setHideCheckin] = useState(false);
  const [expiry, setExpiry] = useState<Expiry>("event");
  const [customDate, setCustomDate] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ url: string; id: string; emails: string[] } | null>(null);
  const [event, setEvent] = useState<any>(null);

  const resetForm = () => {
    setEditing(null);
    setAudience("named");
    setEmails([]);
    setPermission("view");
    setHideMoney(false);
    setHideCheckin(false);
    setExpiry("event");
    setCustomDate("");
    setLabel("");
    setCreated(null);
  };

  const loadInto = (t: any) => {
    setEditing(t);
    const list = (t.allowed_emails ?? []).filter(Boolean);
    setAudience(list.length ? "named" : "anyone");
    setEmails(list);
    setPermission(t.permission === "edit" ? "edit" : "view");
    setHideMoney(!!t.hide_money_columns);
    setHideCheckin(!!t.hide_checkin_columns);
    setExpiry(t.expires_at ? "custom" : "never");
    setCustomDate(toDateInput(t.expires_at));
    setLabel(t.label ?? "");
    setCreated({ url: shareSheetUrl(t.token), id: t.id, emails: list });
    setMode("form");
  };

  const fetchTokens = async () => {
    setLoadingTokens(true);
    const { data, error } = await (supabase as any)
      .from("event_sheet_tokens")
      .select("*")
      .eq("event_id", eventId)
      .eq("scope", scope)
      .eq("tab", tab ?? null)
      .eq("revoked", false)
      .order("created_at", { ascending: false });
    setLoadingTokens(false);
    if (error) {
      console.error("[ShareSheetDialog] token load failed", error);
      return [];
    }
    const rows = data ?? [];
    setTokens(rows);
    return rows;
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    (async () => {
      const [{ data: ev }, rows] = await Promise.all([
        (supabase as any)
          .from("events")
          .select("id, name, end_date, start_date, organization_id")
          .eq("id", eventId)
          .maybeSingle(),
        fetchTokens(),
      ]);
      setEvent(ev ?? null);
      if (rows.length === 1) loadInto(rows[0]);
      else if (rows.length > 1) setMode("list");
      else setMode("form");
    })();
  }, [open, eventId]);

  const expiresAt = (): string | null => {
    const day = (d: Date) => {
      d.setHours(23, 59, 59, 0);
      return d.toISOString();
    };
    if (expiry === "never") return null;
    if (expiry === "7days") return day(new Date(Date.now() + 7 * 864e5));
    if (expiry === "custom") return customDate ? day(new Date(`${customDate}T12:00:00`)) : null;
    const base = event?.end_date || event?.start_date;
    return day(base ? new Date(`${String(base).slice(0, 10)}T12:00:00`) : new Date(Date.now() + 864e5));
  };

  const save = async () => {
    const list = emails;
    if (audience === "named" && list.length === 0) {
      toast.error("Add at least one email address, or switch to anyone with the link.");
      return;
    }
    if (expiry === "custom" && !customDate) {
      toast.error("Pick an expiry date.");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        permission,
        label: label.trim() || defaultShareLabel(),
        allowed_emails: audience === "named" ? list : null,
        hide_money_columns: hideMoney,
        hide_checkin_columns: hideCheckin,
        expires_at: expiresAt(),
        tab: tab ?? null,
      };

      if (editing) {
        const { error } = await (supabase as any)
          .from("event_sheet_tokens")
          .update(payload)
          .eq("id", editing.id);
        if (error) throw error;
        toast.success("Link updated — the existing URL keeps working.");
        setCreated({ url: shareSheetUrl(editing.token), id: editing.id, emails: list });
        onCreated?.();
        await fetchTokens();
        return;
      }

      const { data: userData } = await getSessionUser();
      const { data, error } = await (supabase as any)
        .from("event_sheet_tokens")
        .insert({
          event_id: eventId,
          organization_id: event?.organization_id ?? null,
          scope,
          created_by: userData?.user?.id ?? null,
          ...payload,
        })
        .select("*")
        .single();
      if (error) throw error;

      const url = shareSheetUrl(data.token);
      setEditing(data);
      setCreated({ url, id: data.id, emails: audience === "named" ? list : [] });
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link created and copied.");
      onCreated?.();
      await fetchTokens();
    } catch (e: any) {
      console.error("[ShareSheetDialog] token save failed", e);
      toast.error("Couldn't save the share link. Please try again, or contact support if it keeps happening.");
    } finally {
      setBusy(false);
    }
  };

  const heading = useMemo(() => {
    if (mode === "list") return "Who can see this sheet";
    return editing ? "Edit share link" : "Share this sheet";
  }, [mode, editing]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[100dvh] max-w-lg flex-col p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>
            {mode === "list"
              ? "Pick a link to edit, or create another one."
              : editing
                ? "Changes apply to the link people already have — the URL doesn't change."
                : "A scoped link to the live grid — expiring, revocable, and limited to what you choose."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2">
          {mode === "list" ? (
            <div className="space-y-2">
              {loadingTokens && <Loader2 className="h-4 w-4 animate-spin" />}
              {tokens.map((t) => {
                const list = (t.allowed_emails ?? []).filter(Boolean);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => loadInto(t)}
                    className="w-full rounded-md border p-3 text-left text-sm hover:bg-muted"
                  >
                    <div className="font-medium">{t.label || "Untitled link"}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.permission === "edit" ? "View and edit" : "View only"} ·{" "}
                      {list.length ? `${list.length} named` : "Anyone with the link"} ·{" "}
                      {t.expires_at
                        ? `expires ${new Date(t.expires_at).toLocaleDateString()}`
                        : "no expiry"}
                    </div>
                    {list.length > 0 && (
                      <div className="mt-1 truncate text-xs text-muted-foreground">
                        {list.join(", ")}
                      </div>
                    )}
                  </button>
                );
              })}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  resetForm();
                  setMode("form");
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Create another link
              </Button>
            </div>
          ) : created ? (
            <div className="space-y-3">
              <Label>Link</Label>
              <div className="flex gap-2">
                <Input readOnly value={created.url} className="font-mono text-xs" />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(created.url);
                    toast.success("Copied.");
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="border-t pt-3">
                <SendShareLinkForm
                  tokenId={created.id}
                  recipients={created.emails}
                  syncAllowedEmails={created.emails.length > 0}
                  onSent={() => onOpenChange(false)}
                  onSkip={() => onOpenChange(false)}
                  skipLabel="Close"
                />
              </div>
              <div className="flex gap-2 border-t pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCreated(null)}
                >
                  Edit this link's settings
                </Button>
                {tokens.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setMode("list")}>
                    <ChevronLeft className="mr-1 h-4 w-4" /> All links
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {tokens.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2"
                  onClick={() => setMode("list")}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" /> All links ({tokens.length})
                </Button>
              )}
              <div className="space-y-2">
                <Label>Who can open it</Label>
                <RadioGroup
                  value={audience}
                  onValueChange={(v) => setAudience(v as Audience)}
                  className="space-y-2"
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="named" id="audience-named" />
                    <span className="font-normal">Only people I name</span>
                  </label>
                  {audience === "named" && (
                    <RecipientChipsInput
                      value={emails}
                      onChange={setEmails}
                      placeholder="coach@club.com"
                    />
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="anyone" id="audience-anyone" />
                    <span className="font-normal">Anyone with the link</span>
                  </label>
                </RadioGroup>
                {audience === "anyone" && (
                  <p className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    Anyone this link is forwarded to can open it — including team and contact details.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>What they can do</Label>
                <RadioGroup
                  value={permission}
                  onValueChange={(v) => setPermission(v as "view" | "edit")}
                  className="space-y-2"
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="view" id="perm-view" />
                    <span className="font-normal">View only</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="edit" id="perm-edit" />
                    <span className="font-normal">View and edit</span>
                  </label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Hide from them</Label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={hideMoney}
                    onCheckedChange={(v) => setHideMoney(Boolean(v))}
                  />
                  Money columns — payments, totals, balances
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={hideCheckin}
                    onCheckedChange={(v) => setHideCheckin(Boolean(v))}
                  />
                  Check-in columns — status, roster progress, notes
                </label>
              </div>

              <div className="space-y-2">
                <Label>Expires</Label>
                <Select value={expiry} onValueChange={(v) => setExpiry(v as Expiry)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose expiry" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(EXPIRY_LABELS) as Expiry[]).map((key) => (
                      <SelectItem key={key} value={key}>
                        {EXPIRY_LABELS[key]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {expiry === "custom" && (
                  <Input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                  />
                )}
                {expiry === "never" && (
                  <p className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    A link that never expires stays live until someone remembers to revoke it.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Scorekeeper — Court 3"
                />
                <p className="text-xs text-muted-foreground">
                  Left blank, it saves as "{defaultShareLabel()}".
                </p>
              </div>
            </div>
          )}
        </div>

        {mode === "form" && !created && (
          <div className="shrink-0 border-t px-6 py-4">
            <Button className="w-full" onClick={save} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : "Create link"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
